import { type FC, useEffect, useState } from 'react';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { UiButton, UiDrawer, UiDrawerBody, UiDrawerCloseButton, UiDrawerContent, UiDrawerFooter, UiDrawerOverlay, type UiDrawerProps, UiStack, UiText } from '@/lib/ui';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { requestPasscode, type RequestPasscodeRequest, verifyPasscode, type VerifyPasscodeRequest, type AuthObject } from '@/api/registration';
import { useMutation } from '@tanstack/react-query';
import { type ApiResponseSingle } from '@/api/tenantClient';
import Passcode from '@/base/Passcode/Passcode';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useNavigate } from 'react-router-dom';
import { generatePageUrl } from '@/app/pages';

interface PasscodeDrawerProps extends Omit<UiDrawerProps, 'children'> {
  isOpen: boolean
  email: string | undefined
  onClose: () => void
  groupRegistrationEnabled: boolean
}

const PasscodeDrawer: FC<PasscodeDrawerProps> = ({ isOpen, onClose, email, groupRegistrationEnabled }) => {
  const { eventId, tenantCode } = useRegisterRoute();
  const [errors, setErrors] = useState<string[]>([]);
  const { createTenantApiRequest } = useTenantApi();
  const { setUserSession } = useUserSession();
  const navigate = useNavigate();

  const { mutate: handleRequestPasscode } = useMutation<ApiResponseSingle<void>, Error, RequestPasscodeRequest>({
    mutationFn: async (data: RequestPasscodeRequest) => await requestPasscode(createTenantApiRequest)(data)
  });

  const { mutate: handleVerifyPasscode, isLoading } = useMutation<ApiResponseSingle<AuthObject>, Error, VerifyPasscodeRequest>({
    mutationFn: async (data: VerifyPasscodeRequest) => await verifyPasscode(createTenantApiRequest)(data),
    onSuccess: (data: ApiResponseSingle<AuthObject>) => {
      if (data.errors && data.errors.length > 0) {
        return setErrors(data.errors);
      }
      if (data.item?.uid) {
        setUserSession({ email: data.item.uid, client: data.item.client, authToken: data.item.token });
        if (groupRegistrationEnabled) {
          navigate(generatePageUrl('RegistrationRegisterGroupCategory', { tenantCode, eventId }));
        } else {
          navigate(generatePageUrl('RegistrationRegisterLandingPage', { tenantCode, eventId }));
        }
      }
    }
  });

  return (
    <UiDrawer placement={'right'} onClose={onClose} isOpen={isOpen} size={'lg'}>
      <UiDrawerOverlay />
      <UiDrawerContent>
        <UiDrawerBody p={8}>
          <UiDrawerCloseButton size={'lg'} color={'primary.500'} />
          <UiText variant={'h6'}>Login</UiText>
          {
            email && (
              <Passcode
                errors={errors}
                setErrors={setErrors}
                email={email}
                eventId={eventId}
                isLoading={isLoading}
                onRequestPasscode={handleRequestPasscode}
                onVerifyPasscode={handleVerifyPasscode}
              />
            )
          }
        </UiDrawerBody>
      </UiDrawerContent>
    </UiDrawer>
  );
};

export default PasscodeDrawer;
